import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Building2, 
  Home, 
  TreePine, 
  MapPin, 
  DollarSign,
  Users,
  Calendar,
  Eye,
  Percent,
  TrendingUp,
  Award,
  Star,
  Bed,
  Bath,
  Square
} from "lucide-react";

const RealEstatePage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    initial: {},
    animate: { transition: { staggerChildren: 0.1 } }
  };

  const services = [
    {
      icon: <Home className="w-8 h-8" />,
      title: "Property Flipping",
      description: "Strategic acquisition and renovation of undervalued properties for maximum profit margins and portfolio growth."
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Rental Income Portfolio",
      description: "Cash-flowing rental properties generating consistent monthly income while building long-term equity."
    },
    {
      icon: <TreePine className="w-8 h-8" />,
      title: "Strategic Markets",
      description: "Focused investment in high-growth markets including Las Vegas, Florida, and Detroit for optimal returns."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Proven Track Record",
      description: "Milestone achievement of 10 successful property flips demonstrating consistent execution and market expertise."
    }
  ];

  const metrics = [
    { number: "$850K", label: "Portfolio Value" },
    { number: "3", label: "Active Properties" },
    { number: "$3.5K", label: "Monthly Income" },
    { number: "10", label: "Homes Flipped" }
  ];

  const properties = [
    {
      title: "Las Vegas Investment Property",
      location: "Las Vegas, Nevada",
      status: "Rental Income",
      type: "Single Family",
      description: "Strategic rental property in growing Las Vegas market generating consistent cash flow",
      image: "https://images.unsplash.com/photo-1570206986634-afd7cccb68d3"
    },
    {
      title: "Florida Growth Market Asset",
      location: "Florida",
      status: "Rental Income", 
      type: "Residential",
      description: "High-growth Florida market property positioned for appreciation and rental income",
      image: "https://images.unsplash.com/photo-1582734073424-1cc142558946"
    },
    {
      title: "Detroit Opportunity Zone",
      location: "Detroit, Michigan",
      status: "Rental Income",
      type: "Urban Investment",
      description: "Value-add property in Detroit's revitalization zone with strong rental demand",
      image: "https://images.pexels.com/photos/6809871/pexels-photo-6809871.jpeg"
    }
  ];

  const achievements = [
    {
      metric: "10 Homes",
      description: "Successfully flipped",
      icon: <Home className="w-6 h-6" />
    },
    {
      metric: "$850,000",
      description: "Portfolio value",
      icon: <DollarSign className="w-6 h-6" />
    },
    {
      metric: "3 Markets", 
      description: "Strategic locations",
      icon: <MapPin className="w-6 h-6" />
    },
    {
      metric: "$3,500",
      description: "Monthly rental income",
      icon: <TrendingUp className="w-6 h-6" />
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Sacred Geometry Background */}
        <div className="absolute inset-0 opacity-8">
          <motion.div 
            className="absolute inset-0"
            animate={{ rotate: -360 }}
            transition={{ duration: 180, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='160' height='160' viewBox='0 0 160 160' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23f59e0b' stroke-opacity='0.1' stroke-width='1'%3E%3Cpath d='M80 20L140 50v60L80 140L20 110V50z'/%3E%3Cpath d='M80 40L120 60v40L80 120L40 100V60z'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '160px 160px'
            }}
          />
        </div>

        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1570206986634-afd7cccb68d3"
            alt="Real estate portfolio"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-emerald-900/40"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div 
              className="flex justify-center mb-8"
              variants={fadeInUp}
            >
              <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center shadow-2xl shadow-emerald-400/50">
                <Building2 className="w-12 h-12 text-black" />
              </div>
            </motion.div>

            <motion.h1 
              className="text-6xl md:text-8xl font-bold mb-6"
              variants={fadeInUp}
            >
              Nhalege <span className="text-emerald-400">Real Estate</span>
            </motion.h1>
            
            <motion.p 
              className="text-2xl md:text-3xl text-emerald-400 mb-8 font-light"
              variants={fadeInUp}
            >
              Land is Power
            </motion.p>
            
            <motion.p 
              className="text-xl text-white/80 max-w-4xl mx-auto mb-12 leading-relaxed"
              variants={fadeInUp}
            >
              Building generational wealth through strategic real estate investment. From flipping 10 homes to generating 
              $3,500 monthly rental income, we're proving that smart property investment creates lasting financial freedom.
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center"
              variants={fadeInUp}
            >
              <Link 
                to="/contact"
                className="group bg-gradient-to-r from-emerald-400 to-green-500 text-black px-12 py-4 text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-emerald-400/40 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center space-x-2">
                  <span>View Portfolio</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
              
              <Link 
                to="/contact"
                className="group border-2 border-emerald-400 text-emerald-400 px-12 py-4 text-lg font-bold rounded-full hover:bg-emerald-400 hover:text-black transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5" />
                  <span>Sell to Us</span>
                </div>
              </Link>
              
              <Link 
                to="/contact"
                className="group border-2 border-white/30 text-white px-12 py-4 text-lg font-bold rounded-full hover:bg-white/10 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5" />
                  <span>Partner With Us</span>
                </div>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Portfolio Metrics Section */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {metrics.map((metric, index) => (
              <motion.div 
                key={metric.label}
                className="text-center"
                variants={fadeInUp}
              >
                <div className="text-4xl md:text-5xl font-bold text-emerald-400 mb-2">
                  {metric.number}
                </div>
                <div className="text-white/70 text-sm font-medium tracking-wider uppercase">
                  {metric.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Core Mission Section */}
      <section className="py-24 bg-black relative">
        <div className="absolute inset-0 opacity-5">
          <img 
            src="https://images.unsplash.com/photo-1582734073424-1cc142558946"
            alt="Real estate development"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-5xl md:text-6xl font-bold mb-8">
                Proven <span className="text-emerald-400">Results</span>
              </h2>
              <div className="space-y-6 text-lg text-white/80 leading-relaxed">
                <p>
                  Nhalege Real Estate has built a proven track record with 10 successful property flips 
                  and a growing portfolio of income-generating rental properties.
                </p>
                <p>
                  Our strategic focus on high-growth markets like Las Vegas, Florida, and Detroit 
                  has generated an $850,000 portfolio value while providing $3,500 in monthly rental income.
                </p>
                <p>
                  We believe in <span className="text-emerald-400 font-semibold">
                  strategic market positioning, proven renovation expertise, and long-term wealth building</span> 
                  through real estate ownership and cash flow generation.
                </p>
              </div>
              
              <div className="mt-10 p-8 bg-gradient-to-br from-emerald-400/10 to-transparent border border-emerald-400/20 rounded-2xl">
                <h3 className="text-2xl font-bold text-emerald-400 mb-4">Investment Strategy</h3>
                <ul className="space-y-3 text-white/80">
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                    <span>Focus on cash-flowing properties in growth markets</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                    <span>Strategic flipping for rapid capital appreciation</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                    <span>Long-term rental income for financial stability</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                    <span>Market timing and opportunity identification</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div 
              className="relative"
              variants={fadeInUp}
            >
              <div className="relative">
                <img 
                  src="https://images.pexels.com/photos/6809871/pexels-photo-6809871.jpeg"
                  alt="Property portfolio"
                  className="w-full h-96 object-cover rounded-3xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-3xl"></div>
                
                {/* Floating Achievement Card */}
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-gray-900 to-black border border-emerald-400/30 rounded-2xl p-6 backdrop-blur-lg">
                  <div className="flex items-center space-x-4">
                    <Home className="w-8 h-8 text-emerald-400" />
                    <div>
                      <div className="text-2xl font-bold text-white">10</div>
                      <div className="text-sm text-white/60">Homes Flipped</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Real Estate <span className="text-emerald-400">Expertise</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Four core areas of proven real estate investment and development expertise
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="group bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8 hover:border-emerald-400/50 transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ y: -5 }}
              >
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <span className="text-black">{service.icon}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-emerald-400 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-white/70 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Current Portfolio Section */}
      <section className="py-24 bg-black relative">
        <div className="absolute inset-0 opacity-10">
          <motion.div 
            animate={{ x: [0, 100, 0] }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23f59e0b' stroke-opacity='0.1' stroke-width='1'%3E%3Cpath d='M60 10L105 35v50L60 110L15 85V35z'/%3E%3Cpath d='M60 25L90 42.5v35L60 95L30 77.5v-35z'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '120px 120px'
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Current <span className="text-emerald-400">Portfolio</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Strategic properties across three high-growth markets generating consistent returns
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {properties.map((property, index) => (
              <motion.div
                key={property.title}
                className="group bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-lg border border-emerald-400/20 rounded-3xl overflow-hidden hover:border-emerald-400/50 transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="aspect-video relative">
                  <img 
                    src={property.image}
                    alt={property.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  <div className="absolute top-4 right-4">
                    <div className="bg-emerald-400 text-black px-3 py-1 rounded-full text-sm font-bold">
                      {property.status}
                    </div>
                  </div>
                  <div className="absolute top-4 left-4">
                    <div className="bg-black/60 backdrop-blur-lg text-white px-3 py-1 rounded-full text-sm flex items-center space-x-1">
                      <MapPin className="w-3 h-3" />
                      <span>{property.location}</span>
                    </div>
                  </div>
                </div>
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors duration-300">
                    {property.title}
                  </h3>
                  
                  <div className="text-emerald-400 font-semibold mb-4">{property.type}</div>
                  
                  <p className="text-white/70 mb-6 leading-relaxed">
                    {property.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-white/60">Active Investment</div>
                    <Link 
                      to="/contact"
                      className="bg-gradient-to-r from-emerald-400 to-green-500 text-black px-6 py-2 font-bold rounded-full hover:shadow-lg hover:shadow-emerald-400/30 transition-all duration-300 hover:scale-105 flex items-center space-x-1"
                    >
                      <Eye className="w-4 h-4" />
                      <span>Details</span>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Key <span className="text-emerald-400">Achievements</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Measurable results that demonstrate our real estate investment expertise
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.metric}
                className="group text-center"
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center group-hover:shadow-lg group-hover:shadow-emerald-400/30 transition-all duration-300">
                  <span className="text-black">{achievement.icon}</span>
                </div>
                <h3 className="text-3xl font-bold text-emerald-400 mb-2 group-hover:text-white transition-colors duration-300">
                  {achievement.metric}
                </h3>
                <p className="text-white/70">{achievement.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-br from-emerald-400/10 via-black to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="text-5xl md:text-6xl font-bold mb-8"
              variants={fadeInUp}
            >
              Build Your <span className="text-emerald-400">Legacy</span>
            </motion.h2>
            
            <motion.p 
              className="text-xl text-white/80 mb-12"
              variants={fadeInUp}
            >
              Join our proven real estate investment strategy. From property flipping to rental income, 
              we're building generational wealth through strategic real estate ownership.
            </motion.p>
            
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
              variants={fadeInUp}
            >
              <div className="bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-emerald-400/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-emerald-400 mb-6">Sell Your Property</h3>
                <div className="space-y-4">
                  <input 
                    type="text" 
                    placeholder="Property Address"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-emerald-400"
                  />
                  <input 
                    type="text" 
                    placeholder="Property Type"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-emerald-400"
                  />
                  <input 
                    type="text" 
                    placeholder="Asking Price"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-emerald-400"
                  />
                  <Link 
                    to="/contact"
                    className="w-full bg-gradient-to-r from-emerald-400 to-green-500 text-black py-3 font-bold rounded-full hover:shadow-lg hover:shadow-emerald-400/30 transition-all duration-300 hover:scale-105 flex items-center justify-center"
                  >
                    Get Cash Offer
                  </Link>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-emerald-400/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-emerald-400 mb-6">Investment Inquiry</h3>
                <div className="space-y-4">
                  <input 
                    type="text" 
                    placeholder="Full Name"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-emerald-400"
                  />
                  <input 
                    type="email" 
                    placeholder="Email Address"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-emerald-400"
                  />
                  <select className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-emerald-400">
                    <option value="">Investment Interest</option>
                    <option value="flipping">Property Flipping</option>
                    <option value="rental">Rental Properties</option>
                    <option value="partnership">Investment Partnership</option>
                    <option value="consultation">Market Consultation</option>
                  </select>
                  <Link 
                    to="/contact"
                    className="w-full bg-gradient-to-r from-emerald-400 to-green-500 text-black py-3 font-bold rounded-full hover:shadow-lg hover:shadow-emerald-400/30 transition-all duration-300 hover:scale-105 flex items-center justify-center"
                  >
                    Start Investing
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Navigation Footer */}
      <section className="py-16 bg-gray-900 border-t border-emerald-400/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link 
              to="/contracting"
              className="group flex items-center space-x-2 text-white/70 hover:text-emerald-400 transition-colors duration-300 mb-4 md:mb-0"
            >
              <ArrowRight className="w-5 h-5 rotate-180 group-hover:-translate-x-1 transition-transform" />
              <span>Previous: Contracting</span>
            </Link>
            
            <Link 
              to="/"
              className="text-emerald-400 font-semibold hover:text-white transition-colors duration-300"
            >
              ⬟ Empire Overview
            </Link>
            
            <Link 
              to="/ecommerce"
              className="group flex items-center space-x-2 text-white/70 hover:text-emerald-400 transition-colors duration-300 mt-4 md:mt-0"
            >
              <span>Next: E-Commerce</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RealEstatePage;